/**
 * Upload an asset photo via API upload-url + direct S3 PUT.
 *
 * @param {object} opts
 * @param {(payload: object) => Promise<{ id: number, upload_url: string }>} opts.requestUploadUrl
 *   RTK mutation unwrap that POSTs /assets/photos/upload-url
 * @param {Blob|File|ArrayBuffer} opts.body
 * @param {string} opts.contentType
 * @param {object} opts.uploadPayload - draft_key, mime_type, file_name?, file_size?
 * @returns {Promise<{ id: number }>}
 */
export async function uploadPhotoViaPresignedUrl({
  requestUploadUrl,
  body,
  contentType,
  uploadPayload,
}) {
  const mime = contentType || uploadPayload.mime_type || 'image/jpeg';
  const { id, upload_url: uploadUrl } = await requestUploadUrl({
    ...uploadPayload,
    mime_type: mime,
  });

  if (!uploadUrl) {
    throw new Error('Upload URL missing from API response');
  }

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body,
    headers: {
      'Content-Type': mime,
    },
  });

  if (!putResponse.ok) {
    const text = await putResponse.text().catch(() => '');
    throw new Error(
      `Failed to upload photo to storage (${putResponse.status})${text ? `: ${text}` : ''}`
    );
  }

  return { id };
}

/**
 * Convert a data-URL or raw base64 string to a Blob for S3 PUT.
 */
export function base64ToBlob(base64, mimeType = 'image/jpeg') {
  if (!base64 || typeof base64 !== 'string') {
    throw new Error('Invalid base64 input');
  }

  const hasPrefix = base64.includes(',');
  const data = hasPrefix ? base64.split(',')[1] : base64;
  const mime = hasPrefix ? base64.match(/data:([^;]+);/)?.[1] || mimeType : mimeType;

  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Resolve File/Blob/base64 into a body suitable for S3 PUT.
 */
export function resolveUploadBody(photoData) {
  const mime = photoData.fileType || photoData.mime_type || 'image/jpeg';

  if (photoData.file instanceof Blob) {
    return { body: photoData.file, contentType: photoData.file.type || mime };
  }

  if (photoData.base64) {
    const blob = base64ToBlob(photoData.base64, mime);
    return { body: blob, contentType: mime };
  }

  throw new Error('Missing photo file or base64 data');
}
